import { Container } from "pixi.js"
import { Entity, ScreenDimensions } from "game-common/models"
import { ScalingParams } from "../../../client_util"
import { TranslationComputer } from "./translation_computer"
import { ScreenShakeManager } from "./screen_shake_manager"

export interface CameraManagementServiceConfig {
    surface: Container
    worldOverlaySurface: Container
    screenShakeManager: ScreenShakeManager
}

export class CameraManagementService {
    private surface: Container
    private worldOverlaySurface: Container
    private screenShakeManager: ScreenShakeManager
    private translationComputer: TranslationComputer

    constructor(config: CameraManagementServiceConfig) {
        this.surface = config.surface
        this.worldOverlaySurface = config.worldOverlaySurface
        this.screenShakeManager = config.screenShakeManager
        this.translationComputer = new TranslationComputer()
    }

    /**
     * Update the camera position based on player location
     */
    updatePlayerLocation = (entity: Entity, force?: boolean) => {
        if (!entity) {
            return
        }

        if (entity.location.x < 0 || entity.location.y < 0) {
            return
        }

        // Compute camera translation modifiers
        this.translationComputer.compute(entity.location, force)
        const { modifierX, modifierY } = this.translationComputer

        // Apply screen shake and camera positioning
        const shakeState = this.screenShakeManager.getState()
        if (modifierX !== undefined) {
            const transformedX =
                (shakeState.shakeOffsetX + modifierX + ScreenDimensions.w / 2) * ScalingParams.surfaceZoom -
                48 * ScalingParams.viewportX
            this.surface.transform.position.x = transformedX
            this.worldOverlaySurface.transform.position.x = transformedX
        }
        if (modifierY !== undefined) {
            const transformedY =
                (shakeState.shakeOffsetY + modifierY + ScreenDimensions.h / 2) * ScalingParams.surfaceZoom -
                48 * ScalingParams.viewportY
            this.surface.transform.position.y = transformedY
            this.worldOverlaySurface.transform.position.y = transformedY
        }
    }

    /**
     * Trigger screen shake effect
     */
    screenShake = (entity: Entity) => {
        this.screenShakeManager.screenShake(entity, this.updatePlayerLocation)
    }

    /**
     * Update the translation computer with new tilemap data
     */
    updateTranslationComputer = (tilemapMetaProvider: any) => {
        this.translationComputer.update(tilemapMetaProvider)
    }

    /**
     * Reset camera state
     */
    reset = () => {
        this.translationComputer.reset()
    }

    /**
     * Get the current translation computer for advanced operations
     */
    getTranslationComputer = (): TranslationComputer => {
        return this.translationComputer
    }

    /**
     * Get the current screen shake manager
     */
    getScreenShakeManager = (): ScreenShakeManager => {
        return this.screenShakeManager
    }
}
